/**
 * Chase camera: fixed-lag spring above/behind the ship (spec 3.4).
 * Purely render-side — reads interpolated poses, never sim state.
 */
import { TargetCamera } from "@babylonjs/core/Cameras/targetCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import {
  CAM_AHEAD,
  CAM_BACK,
  CAM_STIFFNESS,
  CAM_UP,
  CAM_X_FOLLOW,
  SHAKE_MS,
  SHAKE_AMPLITUDE,
  ORBIT_TOTAL_DEG,
  ORBIT_DEG_PER_S,
} from "../config/tuning";

export class CameraRig {
  private readonly target = new Vector3();
  private shakeUntil = 0;
  private reducedMotion = false;

  constructor(private readonly camera: TargetCamera) {}

  setReducedMotion(on: boolean): void {
    this.reducedMotion = on;
  }

  /** Screen shake ≤ 120 ms — death & boost pickup only (spec 3.4). */
  shake(): void {
    if (!this.reducedMotion) this.shakeUntil = performance.now() + SHAKE_MS;
  }

  /** Tiny 40 ms head-bump nudge (R2 §2.1) — a fraction of the full shake. */
  nudge(): void {
    if (!this.reducedMotion) this.shakeUntil = performance.now() + 40;
  }

  /** Instantly place the camera (run start / retry). */
  snap(shipX: number, shipY: number, shipZ: number): void {
    this.camera.position.set(shipX * CAM_X_FOLLOW, shipY + CAM_UP, shipZ - CAM_BACK);
    this.lookAt(shipX, shipY, shipZ);
  }

  update(dtSeconds: number, shipX: number, shipY: number, shipZ: number): void {
    const k = 1 - Math.exp(-CAM_STIFFNESS * dtSeconds);
    const p = this.camera.position;
    p.x += (shipX * CAM_X_FOLLOW - p.x) * k;
    p.y += (shipY + CAM_UP - p.y) * k;
    // z follows exactly — forward motion must feel locked, only x/y lag
    p.z = shipZ - CAM_BACK;
    const remaining = this.shakeUntil - performance.now();
    if (remaining > 0) {
      // render-side randomness only — the sim never sees this
      const a = SHAKE_AMPLITUDE * (remaining / SHAKE_MS);
      p.x += (Math.random() - 0.5) * a;
      p.y += (Math.random() - 0.5) * a;
    }
    this.lookAt(shipX, shipY, shipZ);
  }

  private lookAt(shipX: number, shipY: number, shipZ: number): void {
    this.target.set(shipX * 0.8, shipY + 1.1, shipZ + CAM_AHEAD);
    this.camera.setTarget(this.target);
  }

  // --- finish orbit (R2-M4 VFX-5): a slow 90° sweep around the parked ship
  private orbitDeg = -1; // < 0 ⇒ not orbiting

  startOrbit(): void {
    // reduced motion: the camera simply holds its chase framing
    this.orbitDeg = this.reducedMotion ? -1 : 0;
  }

  stopOrbit(): void {
    this.orbitDeg = -1;
  }

  /** Call each frame while results are showing. No-op unless orbiting. */
  orbitUpdate(dtSeconds: number, shipX: number, shipY: number, shipZ: number): void {
    if (this.orbitDeg < 0) return;
    this.orbitDeg = Math.min(ORBIT_TOTAL_DEG, this.orbitDeg + ORBIT_DEG_PER_S * dtSeconds);
    const a = (this.orbitDeg * Math.PI) / 180;
    this.camera.position.set(
      shipX - Math.sin(a) * CAM_BACK,
      shipY + CAM_UP * (1 - this.orbitDeg / ORBIT_TOTAL_DEG * 0.45),
      shipZ - Math.cos(a) * CAM_BACK,
    );
    this.target.set(shipX, shipY + 0.6, shipZ);
    this.camera.setTarget(this.target);
  }
}
